import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Users, Heart, Sparkles, ArrowLeft } from "lucide-react";
import YSVLogo from "@/components/YSVLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CommunityPage = () => {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    organization: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.first_name.trim() || !form.last_name.trim()) { setError("Please enter your first and last name."); return; }
    if (!form.email.trim() || !form.email.includes("@")) { setError("Please enter a valid email."); return; }
    if (!form.phone.trim()) { setError("Please enter a phone number."); return; }
    if (!form.organization.trim()) { setError("Please tell us which youth organization you support."); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/advisory-board-applications`, form);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Thank-you state ─────────────────────────────────────────────
  if (submitted) {
    return (
      <div
        data-testid="community-thankyou"
        style={{
          minHeight: "100vh",
          background: "radial-gradient(1000px 700px at 50% 0%, rgba(0,229,255,0.10), transparent 60%), var(--dark-bg)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "80px 24px 60px",
          textAlign: "center",
        }}
      >
        <div style={{ maxWidth: 900, width: "100%" }}>
          <div style={{ display: "inline-flex", marginBottom: 32 }}>
            <YSVLogo size="hero" />
          </div>

          <h1
            style={{
              fontFamily: "'Outfit', sans-serif",
              fontWeight: 900,
              fontSize: "clamp(72px, 18vw, 180px)",
              lineHeight: 0.95,
              letterSpacing: "-0.03em",
              color: "#fff",
              textShadow: "0 0 40px rgba(0,229,255,0.30)",
              margin: "8px 0 20px",
            }}
          >
            THANK YOU
          </h1>

          <p
            style={{
              fontSize: "clamp(20px, 3vw, 30px)",
              color: "var(--text-secondary)",
              fontWeight: 500,
              maxWidth: 640,
              margin: "0 auto 40px",
              lineHeight: 1.5,
            }}
          >
            We will be in touch.
          </p>

          <Link
            to="/landing"
            data-testid="community-thankyou-home-link"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              color: "var(--cyan)",
              fontSize: 16,
              fontWeight: 600,
              textDecoration: "none",
              padding: "14px 28px",
              border: "1px solid var(--cyan)",
              borderRadius: 999,
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--cyan)";
              e.currentTarget.style.color = "#0a0a0c";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = "var(--cyan)";
            }}
          >
            <ArrowLeft size={18} /> VISIT YSV
          </Link>
        </div>
      </div>
    );
  }

  // ── Form state ──────────────────────────────────────────────────
  return (
    <div
      data-testid="community-page"
      style={{
        padding: "96px 24px 72px",
        minHeight: "100vh",
        background: "var(--dark-bg)",
      }}
    >
      <div style={{ maxWidth: 880, margin: "0 auto" }}>
        {/* Hero */}
        <div style={{ textAlign: "center", marginBottom: 44 }}>
          <p
            style={{
              fontSize: 12,
              color: "var(--cyan)",
              fontWeight: 700,
              letterSpacing: 2,
              textTransform: "uppercase",
            }}
          >
            Community · Team Mom Advisory Board
          </p>
          <h1
            style={{
              fontFamily: "'Outfit', sans-serif",
              fontSize: "clamp(32px, 5vw, 52px)",
              fontWeight: 800,
              color: "#fff",
              marginTop: 8,
              lineHeight: 1.1,
            }}
          >
            Join the Team Mom Advisory Board
          </h1>
          <p
            style={{
              color: "var(--text-secondary)",
              fontSize: "clamp(17px, 2vw, 20px)",
              marginTop: 14,
              maxWidth: 760,
              margin: "14px auto 0",
              lineHeight: 1.75,
            }}
          >
            Team Moms and administrators are the DNA of youth football. We value and appreciate
            your perspective — your selflessness and hard work is essential to everyone's development.
            <br />
            <br />
            At YSV we want to comprise a team of advisors to help us develop a product and service
            that is efficient and beneficial, and help us identify solutions that support organizations.
          </p>
        </div>

        {/* Purpose row */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 16,
            marginBottom: 40,
          }}
        >
          {[
            { icon: Heart, title: "We value your perspective", desc: "You see youth sports from a seat we don't. We want to build with you, not for you." },
            { icon: Users, title: "Help shape YSV", desc: "Advise on features, workflows, and communication — directly with our product team." },
            { icon: Sparkles, title: "Lift your organization", desc: "Help us identify tools and services that make every Team Mom's job lighter." },
          ].map((p, i) => (
            <div
              key={i}
              className="glass-card"
              style={{ padding: 20, textAlign: "left" }}
            >
              <p.icon size={20} style={{ color: "var(--cyan)", marginBottom: 10 }} />
              <div style={{ color: "#fff", fontWeight: 700, fontSize: 15, marginBottom: 6 }}>{p.title}</div>
              <div style={{ color: "var(--text-secondary)", fontSize: 13, lineHeight: 1.55 }}>{p.desc}</div>
            </div>
          ))}
        </div>

        {/* Form */}
        <div className="glass-card" style={{ padding: 32 }}>
          <h2
            style={{
              fontFamily: "'Outfit', sans-serif",
              color: "#fff",
              fontSize: 26,
              fontWeight: 800,
              marginBottom: 6,
            }}
          >
            Be the Difference
          </h2>
          <p style={{ color: "var(--text-secondary)", fontSize: 13, marginBottom: 22 }}>
            We'll follow up personally — no spam.
          </p>

          <form data-testid="community-form" onSubmit={submit} style={{ display: "grid", gap: 16 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>First name *</label>
                <input
                  data-testid="community-first-name"
                  className="input-field"
                  value={form.first_name}
                  onChange={update("first_name")}
                  required
                  style={{ marginTop: 4 }}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>Last name *</label>
                <input
                  data-testid="community-last-name"
                  className="input-field"
                  value={form.last_name}
                  onChange={update("last_name")}
                  required
                  style={{ marginTop: 4 }}
                />
              </div>
            </div>

            <div>
              <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>Email *</label>
              <input
                data-testid="community-email"
                type="email"
                className="input-field"
                value={form.email}
                onChange={update("email")}
                required
                style={{ marginTop: 4 }}
              />
            </div>

            <div>
              <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>Phone number *</label>
              <input
                data-testid="community-phone"
                type="tel"
                className="input-field"
                value={form.phone}
                onChange={update("phone")}
                required
                style={{ marginTop: 4 }}
              />
            </div>

            <div>
              <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                Youth organization you support *
              </label>
              <input
                data-testid="community-organization"
                className="input-field"
                value={form.organization}
                onChange={update("organization")}
                required
                style={{ marginTop: 4 }}
              />
            </div>

            <div>
              <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                How can YSV support youth football?
              </label>
              <textarea
                data-testid="community-support-message"
                className="input-field"
                rows={5}
                placeholder="Share any ideas, pain points, or wins you'd like YSV to help with…"
                value={form.support_message}
                onChange={update("support_message")}
                style={{ marginTop: 4, resize: "vertical", minHeight: 120, fontFamily: "inherit" }}
              />
            </div>

            {error && (
              <p data-testid="community-error" style={{ color: "#f44336", fontSize: 13, margin: 0 }}>
                {error}
              </p>
            )}

            <button
              type="submit"
              data-testid="community-submit"
              disabled={submitting}
              className="btn-primary"
              style={{
                marginTop: 8,
                padding: "14px 20px",
                fontSize: 15,
                fontWeight: 700,
                opacity: submitting ? 0.6 : 1,
              }}
            >
              {submitting ? "Submitting…" : "Submit application"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CommunityPage;
